import { MemberAccountStatusIdEnum } from '../../../models/transactions/enum/member-account-status-id.enum';
import { MemberAccount } from '../../../models/transactions/member-account';
import { DateConversionUtils } from '../../../utils/date-conversion-utils';

const STRUCK_HIDDEN_AFTER_DAYS = 5;

const reginaDateOnlyFormat = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Regina' });

/**
 * The Saskatchewan calendar day of a moment, as UTC midnight so it can be formatted with `timeZone: 'UTC'`.
 * A bare yyyy-MM-dd value is already a calendar day and is taken as is.
 */
function reginaDay(value: string | Date): Date | null {
  const isDateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const dateOnly = isDateOnly ? value : reginaDateOnlyFormat.format(new Date(value));
  const parts = DateConversionUtils.parseDateOnlyParts(dateOnly);
  return parts ? new Date(Date.UTC(parts.year, parts.month - 1, parts.day)) : null;
}

export function statusChangedDay(account: MemberAccount): Date | null {
  return account.statusChangedDate ? reginaDay(account.statusChangedDate) : null;
}

export function struckAccountHiddenAfterDay(account: MemberAccount): Date | null {
  const struckDay = account.isStruck() ? statusChangedDay(account) : null;
  if (!struckDay) return null;
  const hiddenAfterDay = new Date(struckDay);
  hiddenAfterDay.setUTCDate(struckDay.getUTCDate() + STRUCK_HIDDEN_AFTER_DAYS);
  return hiddenAfterDay;
}

export function isStruckAccountHidden(account: MemberAccount, now: Date = new Date()): boolean {
  const hiddenAfterDay = struckAccountHiddenAfterDay(account);
  const today = reginaDay(now);
  return !!hiddenAfterDay && !!today && today > hiddenAfterDay;
}

export function struckAccountDescription(account: MemberAccount, locale: string): string | null {
  const struckDay = statusChangedDay(account);
  const hiddenAfterDay = struckAccountHiddenAfterDay(account);
  if (!struckDay || !hiddenAfterDay) return null;
  const format = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
  const struckDate = format.format(struckDay);
  const hiddenAfterDate = format.format(hiddenAfterDay);
  return $localize`Struck ${struckDate}. Hidden after ${hiddenAfterDate}.`;
}

export function suspendedAccountDescription(account: MemberAccount, locale: string): string | null {
  const suspendedDay = account.statusId === MemberAccountStatusIdEnum.Suspended ? statusChangedDay(account) : null;
  if (!suspendedDay) return null;
  const date = new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(suspendedDay);
  return $localize`Suspended on ${date}`;
}
